import { useState, useEffect } from "react";
import { useNavigate, useLocation, useParams, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import api from "@/lib/api";
import { CalendarRange, AlertCircle, KeyRound, Store } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";

export default function Login() {
  const { login, loginManager, loginStaff } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { tenantSlug } = useParams(); // /:tenantSlug/login の場合のみセットされる
  const [loginId, setLoginId] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [tenantInfo, setTenantInfo] = useState(null);
  const [tenantError, setTenantError] = useState("");

  // Tab: 'manager' | 'staff'
  const [tab, setTab] = useState("manager");

  // 店舗オーナー (manager): 企業ログインID + メアド + PW
  const [mgrLoginId, setMgrLoginId] = useState("");
  const [mgrEmail, setMgrEmail] = useState("");
  const [mgrPw, setMgrPw] = useState("");
  const [mgrErr, setMgrErr] = useState("");
  const [mgrSubmitting, setMgrSubmitting] = useState(false);

  // スタッフ: 企業ログインID + 自分のID + PW
  const [stTenantId, setStTenantId] = useState("");
  const [stLoginId, setStLoginId] = useState("");
  const [stPw, setStPw] = useState("");
  const [stErr, setStErr] = useState("");
  const [stSubmitting, setStSubmitting] = useState(false);

  useEffect(() => {
    if (!tenantSlug) {
      setTenantInfo(null);
      return;
    }
    api.get(`/auth/tenant-info/${encodeURIComponent(tenantSlug)}`)
      .then((r) => { setTenantInfo(r.data); setTenantError(""); })
      .catch(() => setTenantError("この店舗のログインページは存在しません"));
  }, [tenantSlug]);

  // Forgot password state (admin only — staff/manager は店舗管理者に依頼)
  const [forgotId, setForgotId] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [forgotPhase, setForgotPhase] = useState("request"); // request | reset
  const [forgotOpen, setForgotOpen] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const u = await login(loginId.trim(), password, tenantSlug);
      const dest =
        location.state?.from?.pathname ||
        (u.role === "admin" ? "/admin" : u.role === "manager" ? "/manager" : "/staff");
      navigate(dest, { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleManagerLogin = async (e) => {
    e.preventDefault();
    setMgrErr("");
    setMgrSubmitting(true);
    try {
      const u = await loginManager(mgrLoginId.trim(), mgrEmail.trim().toLowerCase(), mgrPw);
      const dest = u.role === "manager" ? "/manager" : u.role === "admin" ? "/admin" : "/staff";
      navigate(dest, { replace: true });
    } catch (err) {
      setMgrErr(formatApiError(err));
    } finally {
      setMgrSubmitting(false);
    }
  };

  const handleStaffLogin = async (e) => {
    e.preventDefault();
    setStErr("");
    setStSubmitting(true);
    try {
      const u = await loginStaff(stTenantId.trim(), stLoginId.trim(), stPw);
      navigate("/staff", { replace: true });
    } catch (err) {
      setStErr(formatApiError(err));
    } finally {
      setStSubmitting(false);
    }
  };

  const requestReset = async () => {
    try {
      const { data } = await api.post("/auth/forgot-password", { login_id: forgotId.trim() });
      if (data.reset_token) {
        setResetToken(data.reset_token);
        setForgotPhase("reset");
        toast.success("再設定トークンを発行しました。次の画面で新しいパスワードを設定してください。");
      } else {
        toast.success(data.message || "再設定情報を確認しました");
      }
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const doReset = async () => {
    try {
      await api.post("/auth/reset-password", { token: resetToken, new_password: newPassword });
      toast.success("パスワードを再設定しました。新しいパスワードでログインしてください。");
      setForgotOpen(false);
      setForgotPhase("request");
      setForgotId("");
      setResetToken("");
      setNewPassword("");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  // 管理者ログインフォームを表示するかどうか (公開ページでは初期非表示)
  // /admin-portal 経由でアクセス、または ?mode=admin クエリ時のみ表示
  const [showAdmin, setShowAdmin] = useState(() => {
    if (typeof window === "undefined") return false;
    const path = window.location.pathname;
    if (path === "/admin-portal" || path.startsWith("/admin-portal/")) return true;
    const params = new URLSearchParams(window.location.search);
    if (params.get("mode") === "admin") return true;
    return false;
  });

  // 店舗ログインURLリマインダー
  const [recoverOpen, setRecoverOpen] = useState(false);
  const [recoverEmail, setRecoverEmail] = useState("");
  const [recoverSending, setRecoverSending] = useState(false);

  const sendRecoverEmail = async () => {
    const e = recoverEmail.trim();
    if (!e || !e.includes("@")) {
      toast.error("正しいメールアドレスを入力してください");
      return;
    }
    setRecoverSending(true);
    try {
      await api.post("/auth/recover-tenant-url", { email: e });
      toast.success("送信しました。届かない場合はメールアドレスをご確認ください");
      setRecoverOpen(false);
      setRecoverEmail("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setRecoverSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-slate-50 to-blue-50 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src="/yasamaru-wave-256.png" alt="やさまる" className="w-28 h-28 mx-auto mb-2" />
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">やさまるシフト</h1>
          <p className="text-sm text-slate-500 mt-1">
            {tenantSlug
              ? "ようこそ！ ログインID と パスワードを入力してください"
              : showAdmin
                ? "管理者ログイン"
                : "ようこそ"}
          </p>
        </div>

        {tenantSlug && (
          <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 flex items-center gap-2.5" data-testid="tenant-banner">
            <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center shrink-0">
              <Store className="w-4 h-4" />
            </div>
            <div className="min-w-0">
              <p className="text-[10px] font-bold text-blue-700 uppercase tracking-wider">店舗別ログイン</p>
              <p className="text-sm font-bold text-slate-900 truncate">
                {tenantInfo?.company || (tenantError ? "(店舗が見つかりません)" : "読み込み中…")}
              </p>
            </div>
          </div>
        )}

        {/* 公開 /login (tenantSlug なし) かつ管理者フォーム非表示時: タブで manager / staff を切替 */}
        {!tenantSlug && !showAdmin && (
          <Card className="p-6 bg-white border border-slate-200 shadow-sm" data-testid="store-user-primary">
            <div className="text-center mb-4">
              <div className="inline-flex w-12 h-12 rounded-full bg-emerald-100 text-emerald-600 items-center justify-center mb-2">
                <Store className="w-6 h-6" />
              </div>
              <h2 className="text-lg font-bold text-slate-900">ご利用中の方はこちら</h2>
            </div>

            {/* タブ */}
            <div className="grid grid-cols-2 gap-1 p-1 bg-slate-100 rounded-lg mb-5" data-testid="login-tabs">
              <button
                type="button"
                onClick={() => setTab("manager")}
                data-testid="tab-manager"
                className={`text-xs font-bold py-2 rounded-md transition-colors ${
                  tab === "manager" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
                }`}
              >
                店舗オーナー
              </button>
              <button
                type="button"
                onClick={() => setTab("staff")}
                data-testid="tab-staff"
                className={`text-xs font-bold py-2 rounded-md transition-colors ${
                  tab === "staff" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
                }`}
              >
                スタッフ
              </button>
            </div>

            {tab === "manager" && (
              <form onSubmit={handleManagerLogin} className="space-y-3" data-testid="manager-login-form">
                {mgrErr && (
                  <div className="rounded-md bg-rose-50 text-rose-700 text-xs px-3 py-2 flex items-center gap-2" data-testid="mgr-login-error">
                    <AlertCircle className="w-3.5 h-3.5 shrink-0" /> {mgrErr}
                  </div>
                )}
                <div>
                  <Label htmlFor="mgr-login-id" className="text-xs">企業ログインID</Label>
                  <Input
                    id="mgr-login-id"
                    type="text"
                    value={mgrLoginId}
                    onChange={(e) => setMgrLoginId(e.target.value)}
                    placeholder="例: yourshop"
                    autoComplete="username"
                    data-testid="mgr-login-id-input"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="mgr-email" className="text-xs">メールアドレス</Label>
                  <Input
                    id="mgr-email"
                    type="email"
                    value={mgrEmail}
                    onChange={(e) => setMgrEmail(e.target.value)}
                    placeholder="your-email@example.com"
                    autoComplete="email"
                    data-testid="mgr-email-input"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="mgr-pw" className="text-xs">パスワード</Label>
                  <Input
                    id="mgr-pw"
                    type="password"
                    value={mgrPw}
                    onChange={(e) => setMgrPw(e.target.value)}
                    autoComplete="current-password"
                    data-testid="mgr-pw-input"
                    required
                  />
                </div>
                <Button
                  type="submit"
                  disabled={mgrSubmitting}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="mgr-login-submit"
                >
                  {mgrSubmitting ? "ログイン中..." : "ログイン"}
                </Button>
                <div className="text-center pt-1">
                  <Link
                    to="/forgot-password"
                    className="text-xs text-slate-500 hover:text-blue-700 underline underline-offset-2"
                    data-testid="mgr-forgot-link"
                  >
                    パスワードをお忘れですか？
                  </Link>
                </div>
              </form>
            )}

            {tab === "staff" && (
              <form onSubmit={handleStaffLogin} className="space-y-3" data-testid="staff-login-form">
                {stErr && (
                  <div className="rounded-md bg-rose-50 text-rose-700 text-xs px-3 py-2 flex items-center gap-2" data-testid="st-login-error">
                    <AlertCircle className="w-3.5 h-3.5 shrink-0" /> {stErr}
                  </div>
                )}
                <div>
                  <Label htmlFor="st-tenant-id" className="text-xs">企業ログインID</Label>
                  <Input
                    id="st-tenant-id"
                    type="text"
                    value={stTenantId}
                    onChange={(e) => setStTenantId(e.target.value)}
                    placeholder="店長から教えてもらった企業ID"
                    autoComplete="organization"
                    data-testid="st-tenant-id-input"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="st-login-id" className="text-xs">ログインID</Label>
                  <Input
                    id="st-login-id"
                    type="text"
                    value={stLoginId}
                    onChange={(e) => setStLoginId(e.target.value)}
                    placeholder="あなたのログインID"
                    autoComplete="username"
                    data-testid="st-login-id-input"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="st-pw" className="text-xs">パスワード</Label>
                  <Input
                    id="st-pw"
                    type="password"
                    value={stPw}
                    onChange={(e) => setStPw(e.target.value)}
                    autoComplete="current-password"
                    data-testid="st-pw-input"
                    required
                  />
                </div>
                <Button
                  type="submit"
                  disabled={stSubmitting}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="st-login-submit"
                >
                  {stSubmitting ? "ログイン中..." : "ログイン"}
                </Button>
                <p className="text-[10px] text-slate-400 text-center mt-2 leading-relaxed">
                  企業ログインIDは、店長からお伝えされたものをご入力ください
                </p>
              </form>
            )}
          </Card>
        )}

        {/* tenantSlug あり、または管理者が表示を要求した場合のみログインフォーム */}
        {(tenantSlug || showAdmin) && (
        <Card className="p-6 bg-white border border-slate-200 shadow-sm">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="login-id" className="text-sm font-medium text-slate-700">
                ログインID
              </Label>
              <Input
                id="login-id"
                data-testid="login-id-input"
                value={loginId}
                onChange={(e) => setLoginId(e.target.value)}
                placeholder="例: staff1"
                autoComplete="username"
                required
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-sm font-medium text-slate-700">
                パスワード
              </Label>
              <Input
                id="password"
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
                className="mt-1.5"
              />
            </div>
            {error && (
              <div
                data-testid="login-error"
                className="flex items-start gap-2 p-3 rounded-md bg-red-50 border border-red-200 text-sm text-red-700"
              >
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}
            <Button
              type="submit"
              data-testid="login-submit-button"
              disabled={submitting}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2.5 h-11"
            >
              {submitting ? "ログイン中..." : "ログイン"}
            </Button>
          </form>

          <div className="mt-5 pt-5 border-t border-slate-100">
            {tenantSlug ? (
              <p className="text-xs text-slate-500" data-testid="tenant-forgot-hint">
                ログインできない場合は、店舗の管理者にパスワード再発行をご依頼ください。
              </p>
            ) : (
            <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
              <DialogTrigger asChild>
                <button
                  data-testid="forgot-password-link"
                  className="text-sm text-blue-600 hover:underline flex items-center gap-1"
                >
                  <KeyRound className="w-4 h-4" /> ログインできない / パスワード再設定
                </button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>パスワード再設定</DialogTitle>
                </DialogHeader>
                {forgotPhase === "request" ? (
                  <div className="space-y-4">
                    <p className="text-sm text-slate-600">
                      ログインIDを入力すると、再設定用トークンを発行します。担当者に分からない場合は管理者へお問い合わせください。
                    </p>
                    <Input
                      data-testid="forgot-loginid-input"
                      value={forgotId}
                      onChange={(e) => setForgotId(e.target.value)}
                      placeholder="ログインID"
                    />
                    <Button
                      data-testid="forgot-request-button"
                      onClick={requestReset}
                      className="w-full bg-blue-600 hover:bg-blue-700"
                    >
                      トークンを発行
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="text-sm">
                      <p className="text-slate-600">再設定トークン:</p>
                      <code className="block p-2 mt-1 rounded bg-slate-100 text-xs break-all">
                        {resetToken}
                      </code>
                    </div>
                    <div>
                      <Label className="text-sm">新しいパスワード</Label>
                      <Input
                        data-testid="forgot-newpw-input"
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className="mt-1.5"
                      />
                    </div>
                    <Button
                      data-testid="forgot-reset-button"
                      onClick={doReset}
                      className="w-full bg-blue-600 hover:bg-blue-700"
                    >
                      パスワードを再設定
                    </Button>
                  </div>
                )}
              </DialogContent>
            </Dialog>
            )}
          </div>
        </Card>
        )}

        <div className="mt-6 text-center text-xs text-slate-500">
          {tenantSlug && "ログインできない場合は店舗の管理者までお問い合わせください"}
        </div>

        {/* 公開 /login の最下部のリンクは表示しない (admin 専用URLは別パス) */}
      </div>
    </div>
  );
}
